import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useConsent, useDeleteAccount } from '../../api/mutations';
import { signOut } from '../../auth/session';
import { BrandMark } from '../../components/BrandMark';
import { ConfirmButton } from '../../components/ConfirmButton';
import { ConsentCheckbox } from '../../components/ConsentCheckbox';
import { Eyebrow } from '../../components/Eyebrow';
import { Headline } from '../../components/Headline';
import { errorMessage } from '../../lib/api';
import styles from './consent.module.css';

function LeaveOptions() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const leave = async (): Promise<void> => {
    queryClient.clear();
    await navigate({ to: '/sign-in' });
  };
  const signOutRequest = useMutation({ mutationFn: signOut, onSuccess: leave });
  const remove = useDeleteAccount();
  return (
    <div className={styles.leave}>
      <p className={styles.note}>Don’t agree? Sign out, or delete your account and everything in it.</p>
      <div className={styles.actions}>
        <button type="button" className="btn btn-outline" disabled={signOutRequest.isPending} onClick={() => signOutRequest.mutate()}>SIGN OUT</button>
        <ConfirmButton
          label="DELETE ACCOUNT & ALL DATA" confirmLabel="YES, DELETE EVERYTHING" prompt="THIS CAN’T BE UNDONE."
          busy={remove.isPending} onConfirm={() => remove.mutate(undefined, { onSuccess: () => void navigate({ to: '/sign-in' }) })}
        />
      </div>
      {[signOutRequest.error, remove.error].map((error, i) => error !== null && <p key={i} role="alert" className="alert-hot">{errorMessage(error)}</p>)}
    </div>
  );
}

export function ConsentPage() {
  const navigate = useNavigate();
  const [agreed, setAgreed] = useState(false);
  const consent = useConsent();
  const onAgree = (): void => consent.mutate(undefined, { onSuccess: () => void navigate({ to: '/' }) });
  return (
    <div className={styles.page}>
      <header className={styles.top}><BrandMark /></header>
      <main className={styles.body}>
        <header className="page-heading">
          <Eyebrow>ONE THING FIRST</Eyebrow>
          <Headline first="YOUR DATA," second="YOUR CALL." size="compact" />
        </header>
        <p className={styles.para}>
          PainPal works out Load, Fitness, Fatigue and Form from your activities, including heart rate, and it needs your agreement to do so.
        </p>
        <ConsentCheckbox checked={agreed} onChange={setAgreed} />
        {consent.isError && <p role="alert" className="alert-hot">{errorMessage(consent.error)}</p>}
        <button type="button" className="btn btn-ink btn-block" disabled={!agreed || consent.isPending} onClick={onAgree}>AGREE AND CONTINUE →</button>
        <LeaveOptions />
        <Link to="/privacy" className={styles.policy}>PRIVACY POLICY</Link>
      </main>
    </div>
  );
}
